import { ClientSideDataGridModel } from "src/components/common/dataGrid/gridModel/ClientSideDataGridModel";
import { ProjectActivityDataGridApi } from "src/api/generated/erp/project/projectActivity/api/projectActivityDataGridApi.ts";
import { FormCrudDataGrid } from "src/components/common/dataGrid/crud/FormCrudDataGrid";
import { ProjectActivityView } from "src/api/generated/erp/db/types/tables/projectActivityView";
import i18n from "i18next";
import { MenuCheckbox } from "src/components/common/contextMenu/MenuCheckbox.tsx";
import { ProjectActivityForm } from "src/components/views/erp/project/projectActivity/ProjectActivityForm.tsx";
import { ProjectActivityContextMenu } from "src/components/views/erp/project/projectActivity/ProjectActivityContextMenu.tsx";
import { nullableAavoObjectRef } from "src/utils/aavoObjectRefUtils.ts";
import { DocumentsOfObjectButton } from "src/components/views/documents/objectDocuments/DocumentsOfObjectButton.tsx";
import {
	dateColumn,
	dateTimeColumn,
	enumColumn,
	integerColumn,
	textColumn,
} from "src/components/common/dataGrid/columns.tsx";
import { getActivityStateLabels } from "src/api/generated/erp/db/types/enums/activityState.ts";
import { CreateNewProjectActivityForm } from "src/components/views/erp/project/projectActivity/CreateNewProjectActivityForm.tsx";
import { RefreshableElementRef } from "src/utils/useRefreshRef.ts";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";

export interface ProjectActivitiesDataGridProps {
	subProjectOrActivityId: {
		type: "subProject" | "activity";
		id: number;
	};
	onSelectionChanged?: (activity: ProjectActivityView | undefined) => void;
	tasksViewRefreshRef: RefreshableElementRef;
}

export const ProjectActivitiesDataGrid = ({
	subProjectOrActivityId,
	onSelectionChanged,
	tasksViewRefreshRef,
}: ProjectActivitiesDataGridProps) => {
	return (
		<ClientSideDataGridModel
			fetchData={({ showCancelledActivities }) =>
				ProjectActivityDataGridApi.getDataGridData({
					subProjectId: subProjectOrActivityId.type === "subProject" ? subProjectOrActivityId.id : null,
					onlyActivityId: subProjectOrActivityId.type === "activity" ? subProjectOrActivityId.id : null,
					cancelledActivities: showCancelledActivities,
				})
			}
			initialParams={{
				showCancelledActivities: false,
			}}
			getRows={(data) => data.activities}
			getRowId={(row) => row.activityId}
			customFunctionUiActionView={"PROJECT_ACTIVITIES"}
			gridId={"65D07E5C2175F4E4"}
			selectFirstRowOnLoad={subProjectOrActivityId.type === "activity"}
			onSelectionChanged={(rows) => onSelectionChanged?.(rows[0])}
			render={({ dataGridProps, data: { subProject }, currentParams, refreshData, onlySelectedRow }) => (
				<FormCrudDataGrid<ProjectActivityView>
					disableMultipleRowSelection={subProjectOrActivityId.type === "activity"}
					columns={[
						textColumn({
							field: "activityName",
							headerName: i18n.t("activity"),
							width: 150,
						}),
						textColumn({
							field: "responsiblePersonName",
							headerName: i18n.t("responsible_person"),
						}),
						textColumn({
							field: "resourceName",
							headerName: i18n.t("resource"),
						}),
						dateColumn({
							field: "plannedStartDate",
							headerName: i18n.t("planned_start"),
						}),
						dateColumn({
							field: "plannedEndDate",
							headerName: i18n.t("planned_end"),
						}),
						enumColumn({
							field: "activityState",
							headerName: i18n.t("state"),
							enumLabels: getActivityStateLabels(),
						}),
						textColumn({
							field: "note",
							headerName: i18n.t("note"),
						}),
						dateTimeColumn({
							field: "creationDate",
							headerName: i18n.t("created_at"),
						}),
						textColumn({
							field: "createdByUserName",
							headerName: i18n.t("created_by"),
						}),
						dateTimeColumn({
							field: "releasedDate",
							headerName: i18n.t("released_at"),
						}),
						textColumn({
							field: "releasedByUserName",
							headerName: i18n.t("released_by"),
						}),
						dateTimeColumn({
							field: "actualStartDate",
							headerName: i18n.t("started_at"),
						}),
						textColumn({
							field: "startedByUserName",
							headerName: i18n.t("started_by"),
						}),
						dateTimeColumn({
							field: "actualEndDate",
							headerName: i18n.t("completed_at"),
						}),
						textColumn({
							field: "completedByUserName",
							headerName: i18n.t("completed_by"),
						}),
						dateTimeColumn({
							field: "cancelledDate",
							headerName: i18n.t("cancelled_at"),
						}),
						textColumn({
							field: "cancelledByUserName",
							headerName: i18n.t("cancelled_by"),
						}),
						integerColumn({
							field: "activityId",
							headerName: i18n.t("id"),
						}),
					]}
					actionBarComponents={
						<>
							<DocumentsOfObjectButton
								objectRef={nullableAavoObjectRef("PROJECT_ACTIVITY", onlySelectedRow?.activityId)}
							/>
						</>
					}
					actionBarMenuComponents={[
						<MenuCheckbox
							key={"showCancelled"}
							label={i18n.t("show_cancelled")}
							checked={currentParams.showCancelledActivities}
							onChange={(checked) => refreshData({ showCancelledActivities: checked })}
						/>,
					]}
					rowContextMenuComponents={({ row, allSelectedRows }) => (
						<ProjectActivityContextMenu
							activity={row}
							allSelectedActivities={allSelectedRows}
							refreshData={refreshData}
							tasksViewRefreshRef={tasksViewRefreshRef}
						/>
					)}
					form={{
						addRowEnabled: ["INITIAL", "RELEASED"].includes(subProject.subProjectState),
						editEnabled: true,
						dialogTitle: (row) => (row == null ? i18n.t("new_activity") : i18n.t("activity")),
						dialogSize: (row) => (row == null ? "sm" : "md"),
						agentContext: (row) => ({
							key: defineAgentUiContextKey("erp.project.projectActivity.projectActivitiesDataGrid.formDialog"),
							title: row == null ? i18n.t("new_activity") : i18n.t("activity"),
							pathSegments: row == null ? undefined : [row.activityName],
							data: {
								subProjectId: subProject.subProjectId,
								activityId: row?.activityId ?? null,
							},
						}),
						component: ({ row, closeDialog, ...other }) =>
							row == null ?
								<CreateNewProjectActivityForm
									subProjectId={subProject.subProjectId}
									closeDialog={closeDialog}
									{...other}
								/>
							:	<ProjectActivityForm
									subProjectId={subProject.subProjectId}
									activityId={row?.activityId ?? null}
									{...other}
								/>,
					}}
					agentContext={{
						key: defineAgentUiContextKey("erp.project.projectActivities.grid"),
						data: {
							subProjectId:
								subProjectOrActivityId.type === "subProject" ? subProjectOrActivityId.id : null,
							activityId: subProjectOrActivityId.type === "activity" ? subProjectOrActivityId.id : null,
						},
						rowFields: ["activityId", "subProjectId", "projectId", "activityName", "activityState"],
						selectedRowPathSegment: (row) => row.activityName,
					}}
					{...dataGridProps}
				/>
			)}
		/>
	);
};
